import { AsyncSubject, Observable } from 'rxjs';
import {
  EstimatedPickupTime,
  OnHoldTripLegApproveDenyAction,
  Reservation,
  ReservationValidations,
  StandingOrder,
  TripLeg,
  TripLegPackage,
} from '../data/models/reservation.model';
import { RejectTripLegRequest } from '../data/models/trip-leg-reject.model';
import {
  TransportReservationResponse,
  TransportReservationRequest,
} from '../data/models/transport-reservation.model';
import httpService from './httpService';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '../data/models/trip-leg-status.model';

class ReservationService {
  private readonly apiBase;
  private readonly apiTransportBase;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_RESERVATION_API_URL.trim();
    this.apiTransportBase =
      __environment__.REACT_APP_TRANSPORT_RESERVATION_API_URL.trim();
  }

  public getReservationByReferenceID(
    reservationReferenceID: string,
    passengerUUID: string,
  ): Observable<Reservation> {
    const url = `${this.apiBase}/getReservation?reservationReferenceID=${reservationReferenceID}&passengerUUID=${passengerUUID}`;
    return httpService.getJSON(url);
  }

  public getPassengerReservations(
    passengerUUID: string,
    completed: boolean,
    pageSize: number,
    nextPageToken?: string,
  ): Observable<{ reservations: Reservation[]; continuationToken?: string }> {
    let url = `${
      this.apiBase
    }/getReservations?passengerUUID=${encodeURIComponent(
      passengerUUID,
    )}&completed=${completed}&pageSize=${pageSize}`;
    if (nextPageToken) {
      url += `&continuationToken=${encodeURIComponent(nextPageToken)}`;
    }
    return httpService.getJSON(url);
  }

  public getPassengerTransportReservations(
    request: TransportReservationRequest,
    signal?: AbortSignal,
  ): Observable<TransportReservationResponse> {
    const params = httpService.getQueryParams(request);
    return httpService.getJSON(`${this.apiTransportBase}?${params}`, signal);
  }

  public validateReservation(
    reservation: any,
    signal?: AbortSignal,
  ): Observable<ReservationValidations> {
    const url = `${this.apiBase}/validate`;
    return httpService.postJSON(url, reservation, false, signal);
  }

  public createReservation = (
    reservation: Reservation,
    standingOrder: StandingOrder | undefined,
    saveAsDraft: boolean,
  ): Observable<Reservation> => {
    const url = `${this.apiBase}/createReservation`;
    return httpService.postJSON(url, {
      saveAsDraft,
      reservation,
      standingOrder,
    });
  };

  public cancelReservation(
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegStatusReasonBCs: string[],
  ): Observable<void> {
    const payload = {
      passengerUUID,
      reservationReferenceID,
      tripLegStatusReasonBCs,
    };
    return httpService.postJSON(`${this.apiBase}/cancelReservation`, payload);
  }

  public cancelStandingOrderReservation(
    standingOrderUUID: string,
    tripLegStatusReasonBCs: string[],
  ): Observable<void> {
    const payload = {
      standingOrderUUID,
      tripLegStatusReasonBCs,
    };
    return httpService.postJSON(`${this.apiBase}/cancelStandingOrder`, payload);
  }

  public getTripLeg(
    passengerUUID: string,
    reservationID: string,
    tripLegID: string,
  ): Observable<TripLeg> {
    const pid = encodeURIComponent(passengerUUID);
    const rid = encodeURIComponent(reservationID);
    const lid = encodeURIComponent(tripLegID);
    const url = `${this.apiBase}/getTripLeg?passengerUUID=${pid}&reservationReferenceID=${rid}&tripLegReferenceID=${lid}`;
    return httpService.getJSON(url);
  }

  public getAllTripLegs(
    state: string | null,
    appointmentDate: string | null,
  ): Observable<{ tripLegs: TripLeg[] }> {
    const params: { state?: string; appointmentDate?: string } = {};
    if (state) {
      params.state = state;
    }
    if (appointmentDate) {
      params.appointmentDate = appointmentDate;
    }
    const url = `${this.apiBase}/getAllTripLegs?${new URLSearchParams(params).toString()}`;
    return httpService.getJSON(url);
  }

  public getDraftTripLegs(
    status: string | null = 'onhold',
  ): Observable<{ tripLegs: TripLeg[] }> {
    const url = `${this.apiBase}/getDraftTripLegs?status=${status}`;
    return httpService.getJSON(url);
  }

  public reopenRejectedTripLeg(
    passengerUUID: string,
    tripLegReferenceID: string,
  ): Observable<TripLeg> {
    const payload = {
      passengerUUID,
      tripLegReferenceID,
    };
    return httpService.postJSON(
      `${this.apiBase}/reopenRejectedTripLeg`,
      payload,
    );
  }

  public addTripLeg = (
    reservationReferenceID: string,
    tripLeg: TripLeg,
    saveAsDraft: boolean,
  ): Observable<TripLeg> => {
    const url = `${this.apiBase}/addTripLeg`;
    const legData = { ...tripLeg, reservationReferenceID };
    return httpService.postJSON(url, {
      saveAsDraft,
      tripLeg: legData,
    });
  };

  public updateTripLegs(
    tripLegs: TripLeg[],
    saveAsDraft: boolean,
  ): Observable<TripLeg[]> {
    return httpService.postJSON(`${this.apiBase}/updateTripLegs`, {
      saveAsDraft,
      tripLegs,
    });
  }

  public cancelTripLeg(
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceID: string,
    tripLegStatusReasonBCs: string[],
  ): Observable<Reservation> {
    const legInfo = {
      passengerUUID,
      reservationReferenceID,
      tripLegReferenceID,
      tripLegStatusReasonBCs,
    };
    return httpService.postJSON(`${this.apiBase}/cancelTripLeg`, legInfo);
  }

  public rejectTripLeg(payload: RejectTripLegRequest): Observable<TripLeg> {
    return httpService.postJSON(`${this.apiBase}/rejectTripLeg`, payload);
  }

  public reorderTripLegs(
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceIDs: string[],
  ): Observable<Reservation> {
    const payload = {
      passengerUUID,
      reservationReferenceID,
      tripLegReferenceIDs,
    };
    return httpService.postJSON(`${this.apiBase}/resequenceTripLegs`, payload);
  }

  public approveOrDenyTripLegsOnHold(
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceIDs: string[],
    action: OnHoldTripLegApproveDenyAction,
  ) {
    const payload = {
      passengerUUID,
      reservationReferenceID,
      tripLegReferenceIDs,
      action,
    };
    return httpService.postJSON(
      `${this.apiBase}/approveOrDenyTripLegsOnHold`,
      payload,
    );
  }

  public getPassengerBenefitSummary(
    passengerUUID: string,
    servicePlanUUIDs: string[],
    appointmentDate: string,
  ) {
    return httpService.getJSON(
      `${this.apiBase}/getPassengerBenefitSummary/?passengerUUID=${passengerUUID}&appointmentDate=${appointmentDate}&servicePlanUUIDs=${servicePlanUUIDs.join(',')}`,
    );
  }

  public updateTripLegStatus(
    passengerUUID: string,
    tripLegReferenceID: string,
    action: string,
    tripLegStatusDateTimeUTC: string,
    tripLegStatusReasonBCs?: string[],
  ): Observable<any> {
    const url = `${this.apiBase}/driverAction`;
    return httpService.postJSON(url, {
      passengerUUID,
      tripLegReferenceID,
      action,
      tripLegStatusDateTimeUTC,
      tripLegStatusReasonBCs,
    });
  }

  public updateTripLegLog(
    status: TripLegStatusBC,
    passengerUUID: string,
    tripLegReferenceID: string,
    tripLegStatusDateTimeUTC: string,
  ): Observable<TripLegPackage> {
    const url = `${this.apiBase}/updateTripLegLog`;
    return httpService.postJSON(url, {
      status: status.toLowerCase(),
      passengerUUID,
      tripLegReferenceID,
      tripLegStatusDateTimeUTC,
    });
  }

  public overrideTripLegStatus(
    passengerUUID: string,
    tripLegReferenceID: string,
    status: TripLegStatus,
    tripLegStatusDateTimeUTC: string,
    tripLegStatusReasonBCs?: string[] | null,
  ): Observable<TripLegPackage> {
    const url = `${this.apiBase}/overrideTripLegStatus`;
    return httpService.postJSON(url, {
      passengerUUID,
      tripLegReferenceID,
      status,
      tripLegStatusDateTimeUTC,
      tripLegStatusReasonBCs,
    });
  }

  public getReservationNarrative(
    reservation: Reservation,
    standingOrder: StandingOrder | undefined,
  ): Observable<any> {
    const url = `${this.apiTransportBase}/narrative`;
    return httpService.postJSON(url, { reservation });
  }

  public getEstimatedTripTime(
    pickUpAddress: any,
    dropOffAddress: any,
  ): Observable<EstimatedPickupTime> {
    const url = `${this.apiTransportBase}/calculate-pickup-time`;
    return httpService.postJSON(url, { pickUpAddress, dropOffAddress });
  }
}

const reservationService = new ReservationService();

export default reservationService;
